/**
 * LA NOUVELLE COMMANDE FOURNISSEUR — l'éditeur de la maquette (`acNc`, modale à 90 % de l'écran).
 *
 * UTILITÉ : on ne choisit plus un fournisseur. On cherche ou on bipe un article ; il arrive sous son
 * fournisseur avec une grille coloris × taille, son dernier prix d'achat et les conditions du fournisseur
 * (modifiables pour cette commande). Une commande part par fournisseur — en brouillon, ou validée — sous un
 * numéro PROPOSÉ et modifiable, à la date de commande choisie.
 *
 * ⚠️ AUCUNE NOTE (décision utilisateur) : ni référence interne, ni note au fournisseur.
 * ⚠️ VALIDER PUIS ENVOYER (D1) : chaque commande validée dont la fiche fournisseur porte une adresse part en PDF par la
 *    messagerie du MAGASIN ; les autres sont « à transmettre », et un dialogue propose leur PDF. Le bouton ne dit
 *    « envoyer » que pour ce qui partira vraiment — un écran qui annoncerait un envoi qui n'a pas lieu mentirait (§23.0).
 * ⚠️ L'ESCOMPTE SE MODIFIE ICI (décision utilisateur Q4), comme la remise et le port : le dessin ne
 *    l'offrait qu'en lecture, la décision le fige et le rend modifiable par commande.
 * ⚠️ UNE MARQUE SANS FOURNISSEUR A SON GROUPE, « fournisseur à définir » (décision Q7) : on la
 *    rattache d'ici, une fois pour toutes dans les référentiels ; d'ici là elle part en brouillon, jamais validée.
 *    Un article sans marque NI fournisseur n'entre pas (D4) : il n'y aurait rien à rattacher.
 * ⚠️ LE MÊME ÉDITEUR SAISIT UN BL (`kind="deliveryNote"`) : un BL par fournisseur, au RÉEL
 *    reçu — ni remise ni port —, enregistré pour être intégré depuis la liste, ou intégré tout de suite, entrée
 *    datée du BL. « Une commande ouverte » reprend son reste à livrer, au prix NET de la commande, et y rattache le
 *    BL. Une marque sans fournisseur s'enregistre, elle n'entre pas en stock.
 * ⚠️ TOUT CALCUL EST DANS `order-editor-grid.ts` (pur, éprouvé) et dans le CŒUR des montants.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { netUnitPriceFine } from '@orgazone/core-inventory';
import { confirm, Icon, ModalBackdrop, Picker, showToast, Trans, useOverlayPanel } from '@orgazone/ui-web';
import { masterDataId, typedDocumentNumber } from '@orgazone/sync-contract';
import { api, cleDeGeste, type CommandeResume, type OrderBrand, type OrderGroupInput, type OrderModel, type OrderModelHit } from '../api';
import { currentCurrency } from '@orgazone/money-format';
import { euros } from './titres';
import { t } from '../i18n';
import { fieldLabel } from '../vocabulary';
import {
  batchOf, cardPieces, civilOf, columnTotals, conditionsFromTerms, deliveryBatchOf, duplicateRow, fillAll, filledGroups, fineFromInput, gridOf,
  groupKeyOf, groupsOf, groupTotals, inputFromFine, mergeQuantities, numbersOf, orderableQty, orderDateProblem, orderEmailOf, readConditions,
  sendPlan,
  readQty, rowTotal, todayCivil,
  type BatchProblem, type EditorCard, type EditorConditions, type EditorKind, type Grid, type GroupNumber, type LoadedDraft, type NumberProblem,
} from './order-editor-grid';

const MONO = 'var(--font-mono)';

/** Une date civile locale `AAAA-MM-JJ`, décalée de `days` jours — les bornes du réassort. */
function localDay(days = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Le prix pré-rempli et son origine, pour l'infobulle du champ PA. */
function priceHint(m: OrderModel, kind: EditorKind): string {
  if (kind === 'deliveryNote') {
    return m.sheetPrice != null ? t('Prix de la fiche article — ou celui de la commande reprise.') : t('Aucun prix connu — saisissez le prix d’achat.');
  }
  const p = m.lastPrice;
  if (!p) return t('Aucun prix connu — saisissez le prix d’achat.');
  if (p.origin === 'sheet') return t('Prix de la fiche article — aucune réception encore.');
  return t('Dernier prix de commande, le {date}', { date: new Date(p.at ?? 0).toLocaleDateString() });
}

/**
 * Le mot d'un champ de conditions refusé — l'écran nomme ce qu'il faut corriger.
 */
function conditionField(f: string): string {
  switch (f) {
    case 'discount': return t('la remise');
    case 'shipping': return t('le port');
    case 'earlyPayment': return t('l’escompte');
    default: return t('le délai d’escompte');
  }
}

/** Pourquoi un numéro ne part pas, dans les mots de la maquette. */
function numberProblemText(p: NumberProblem, kind: EditorKind): string {
  switch (p) {
    case 'empty': return kind === 'order' ? t('Saisissez un numéro de commande.') : t('Saisissez un numéro de BL.');
    case 'bad': return t('Lettres, chiffres et « - _ / . » seulement, sans espace.');
    case 'taken': return t('Ce numéro existe déjà — choisissez-en un autre.');
    default: return t('Numéro identique à celui d’un autre fournisseur de cette saisie.');
  }
}

/** Pourquoi une date de commande ne part pas. */
const dateProblemText = (p: 'missing' | 'future'): string =>
  (p === 'missing' ? t('Saisissez la date.') : t('La date ne peut pas être dans le futur.'));

/** Le libellé d'une case de la grille — coloris, taille et second axe, pour le message du bip. */
function cellLabel(m: OrderModel, gencode: string): string {
  const c = m.cells.find((x) => x.gencode === gencode);
  return c ? [c.color, c.size, c.size2].filter(Boolean).join(' ') : gencode;
}

export function OrderEditor({ kind = 'order', draftNumber, takenNumbers, onClose, onSaved }: {
  /** Une commande (défaut), ou un BL — même grille, même panneau, autre lot. */
  kind?: EditorKind;
  /** Le brouillon de commande à reprendre — absent pour une commande neuve, toujours pour un BL. */
  draftNumber?: string | null;
  /**
   * Les numéros déjà en base pour ce document (commandes, ou BL), lus par la liste : l'écran ne PROPOSE pas un
   * numéro pris, et le dit s'il est tapé. Le serveur reste juge — un autre poste a pu le prendre depuis.
   */
  takenNumbers: readonly string[];
  onClose: () => void;
  /**
   * Des documents sont enregistrés. `stillOpen` : des marques sans fournisseur sont restées dans l'éditeur —
   * la liste se relit, l'éditeur ne se ferme pas.
   */
  onSaved: (numbers: string[], stillOpen: boolean) => void;
}) {
  const isBl = kind === 'deliveryNote';
  const [cards, setCards] = useState<EditorCard[]>([]);
  const [conds, setConds] = useState<Record<string, EditorConditions>>({});
  const [loadedDrafts, setLoadedDrafts] = useState<LoadedDraft[]>([]);
  const [loading, setLoading] = useState(!!draftNumber);
  const [notice, setNotice] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<OrderModelHit[] | null>(null);
  /** Le terme dont `hits` est la réponse — la liste affichée peut être celle d'une frappe précédente. */
  const [hitsFor, setHitsFor] = useState('');
  const [hi, setHi] = useState(0);
  const [searchErr, setSearchErr] = useState<string | null>(null);
  /** Ce que le dernier bip a fait — l'opérateur qui bipe sans regarder la grille doit savoir qu'il a compté. */
  const [flash, setFlash] = useState<string | null>(null);
  const [tool, setTool] = useState<'' | 'dup' | 'reas'>('');
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  /** Les fournisseurs du référentiel — lus dès qu'une marque attend d'être rattachée. */
  const [suppliers, setSuppliers] = useState<{ code: string; label: string }[] | null>(null);
  const [suppliersErr, setSuppliersErr] = useState<string | null>(null);
  /** La commande que chaque BL solde, par groupe — posée par « Une commande ouverte », retirée d'un clic. */
  const [links, setLinks] = useState<Record<string, string>>({});
  /**
   * La messagerie du magasin peut-elle envoyer ? `null` tant qu'on ne sait pas — et une lecture ratée reste `null` :
   * on ne sait rien, donc rien ne s'annonce envoyé.
   */
  const [mail, setMail] = useState<{ ready: boolean; why: string } | null>(null);
  // ⚠️ UNE CLÉ PAR OUVERTURE DE L'ÉDITEUR, pas par clic : un double clic rejoue la même clé, et le serveur
  //    rend le document déjà écrit au lieu d'en créer un second.
  const gestureKey = useRef(cleDeGeste(isBl ? 'deliveries:editor' : 'orders:editor', Date.now()));
  const searchRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  // ── Reprendre un brouillon ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!draftNumber) return;
    let alive = true;
    void (async () => {
      try {
        const order = await api.order(draftNumber);
        const keys = [...new Set(order.lines.map((l) => l.modelKey).filter((k): k is string => !!k))];
        const models = keys.length ? (await api.orderEditorModels(keys)).rows : [];
        // ⚠️ UNE LIGNE QUE LA GRILLE NE PEUT PAS MONTRER NE SE REPREND PAS EN SILENCE — article sorti du
        //    catalogue, déclinaison sans case (archivée), modèle sans fournisseur NI marque : elle serait
        //    invisible à l'écran et disparaîtrait au prochain enregistrement. On la COMPTE et on le dit.
        let lost = order.lines.filter((l) => !l.modelKey || !models.some((m) => m.modelKey === l.modelKey)).length;
        const incoming: { model: OrderModel; qty: Record<string, number>; pa: string }[] = [];
        const otherTerms: Record<string, EditorConditions> = {};
        // ⚠️ LE BROUILLON GARDE SON IDENTITÉ sous le groupe de SES articles : son fournisseur — ou, né sans
        //    (marque à rattacher), le groupe où ses articles tombent AUJOURD'HUI. Une marque rattachée depuis
        //    l'emmène donc sous son fournisseur, avec son numéro.
        let draftKey: string | null = order.supplierId;
        let draftModel: OrderModel | null = null;
        for (const m of models) {
          const lines = order.lines.filter((l) => l.modelKey === m.modelKey);
          const key = groupKeyOf(m);
          if (!key) { lost += lines.length; continue; }
          const kept = orderableQty(m, Object.fromEntries(lines.map((l) => [l.gencode, l.orderedQty])));
          lost += kept.dropped;
          if (Object.keys(kept.qty).length === 0) continue;
          draftKey ??= key;
          if (key === draftKey) draftModel ??= m;
          // ⚠️ Une marque passée chez un autre fournisseur emmène sa carte sous lui, avec SES conditions.
          else otherTerms[key] = conditionsFromTerms(m.supplier?.terms ?? null);
          const priced = lines.find((l) => l.unitPriceFine != null);
          incoming.push({ model: m, qty: kept.qty, pa: inputFromFine(priced?.unitPriceFine ?? null) });
        }
        if (!alive) return;
        setCards(mergeQuantities([], incoming, 'add'));
        // ⚠️ Un brouillon né sans fournisseur n'a pas de conditions : rattaché depuis, il prend celles de la fiche.
        const draftTerms = order.supplierId ? {
          discountBp: order.discountBp, shippingCents: order.shippingCents, earlyPaymentBp: order.earlyPaymentBp,
          earlyPaymentDays: order.earlyPaymentDays, accountCode: null, address1: null, address2: null, postalCode: null,
          city: null, country: null, orderEmail: null,
        } : draftModel?.supplier?.terms ?? null;
        setConds({
          ...otherTerms,
          ...(draftKey ? {
            [draftKey]: conditionsFromTerms(draftTerms, order.number, order.updatedAt, order.orderedOn != null ? civilOf(order.orderedOn) : todayCivil()),
          } : {}),
        });
        setLoadedDrafts([{ number: order.number, updatedAt: order.updatedAt }]);
        if (lost > 0) setNotice(t('{n} ligne du brouillon ne se reprend pas : son article n’est plus commandable (hors catalogue, archivé, ou ni fournisseur ni marque).', { n: lost }));
      } catch (e) {
        if (alive) setNotice(t('Le brouillon n’a pas pu être relu : {motif}', { motif: e instanceof Error ? e.message : String(e) }));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [draftNumber]);

  // ── La recherche ──────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2 || /^\d{8,14}$/.test(term)) { setHits(null); return; }
    let alive = true;
    const timer = setTimeout(() => {
      api.orderEditorSearch(term)
        .then((r) => { if (alive) { setHits(r.rows); setHitsFor(term); setHi(0); } })
        .catch((e) => { if (alive) setSearchErr(e instanceof Error ? e.message : String(e)); });
    }, 200);
    return () => { alive = false; clearTimeout(timer); };
  }, [q]);

  const focusCell = (modelKey: string) => setTimeout(() => {
    listRef.current?.querySelector<HTMLInputElement>(`[data-cell^="${CSS.escape(modelKey)}|"]`)?.focus();
  }, 40);

  /**
   * Ajoute un article à la saisie (s'il n'y est pas), avec les conditions de son fournisseur.
   * ⚠️ UN BIP AJOUTE UNE PIÈCE dans la case bipée et laisse le curseur dans la recherche : on enchaîne
   *    les codes-barres sans toucher la souris.
   */
  const addModel = useCallback(async (modelKey: string, scannedGencode: string | null) => {
    setSearchErr(null);
    setFlash(null);
    try {
      const existing = cards.find((c) => c.model.modelKey === modelKey);
      const model = existing?.model ?? (await api.orderEditorModels([modelKey])).rows[0];
      if (!model) { setSearchErr(t('Cet article n’est plus au catalogue.')); return; }
      const key = groupKeyOf(model);
      if (!key) {
        setSearchErr(t('« {name} » n’a ni fournisseur ni marque : renseignez sa fiche article.', { name: model.name }));
        return;
      }
      if (model.cells.length === 0) {
        setSearchErr(t('« {name} » n’a aucune déclinaison avec code-barres : elle ne pourrait pas être reçue sur un BL.', { name: model.name }));
        return;
      }
      // ⚠️ UN BL NE PORTE AUCUNE CONDITION : une réception n'a ni remise ni port à recopier.
      const terms = isBl ? null : model.supplier?.terms ?? null;
      setConds((c) => (c[key] ? c : { ...c, [key]: conditionsFromTerms(terms) }));
      setCards((cs) => mergeQuantities(cs, [{ model, qty: scannedGencode ? { [scannedGencode]: 1 } : {} }], 'add', kind));
      setQ('');
      setHits(null);
      if (scannedGencode) {
        const after = (existing?.qty[scannedGencode] ?? 0) + 1;
        setFlash(t('+1 · {name} · {cell} → {n} pièce', { name: model.name, cell: cellLabel(model, scannedGencode), n: after }));
        searchRef.current?.focus();
      } else focusCell(modelKey);
    } catch (e) {
      setSearchErr(e instanceof Error ? e.message : String(e));
    }
  }, [cards, isBl, kind]);

  const onSearchKey = async (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!hits?.length) return;
      e.preventDefault();
      setHi((h) => (h + (e.key === 'ArrowDown' ? 1 : hits.length - 1)) % hits.length);
      return;
    }
    if (e.key === 'Escape') { if (q) { e.preventDefault(); e.stopPropagation(); setQ(''); setSearchErr(null); } return; }
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const term = q.trim();
    if (!term) return;
    if (/^\d{8,14}$/.test(term)) {
      try {
        const r = await api.orderEditorSearch(term);
        const hit = r.rows[0];
        if (!hit?.scanned) { setFlash(null); setSearchErr(t('Gencode {code} inconnu du catalogue — rien n’a été ajouté.', { code: term })); return; }
        await addModel(hit.modelKey, hit.scanned.gencode);
      } catch (err) { setSearchErr(err instanceof Error ? err.message : String(err)); }
      return;
    }
    try {
      // ⚠️ LA LISTE AFFICHÉE PEUT ÊTRE CELLE D'UNE FRAPPE PRÉCÉDENTE (la recherche attend 200 ms) : une Entrée
      //    tapée avant sa réponse cherche le terme ACTUEL — sinon on ajouterait un autre article, ou on dirait
      //    introuvable un article qui existe.
      const fresh = hitsFor === term && hits !== null;
      const rows = fresh ? hits : (await api.orderEditorSearch(term)).rows;
      const pick = rows[fresh ? Math.min(hi, rows.length - 1) : 0];
      if (pick) { await addModel(pick.modelKey, null); return; }
      setSearchErr(t('Aucun article ne correspond.'));
    } catch (err) { setSearchErr(err instanceof Error ? err.message : String(err)); }
  };

  // ── Le rendu dérivé ───────────────────────────────────────────────────────────────────────────
  const groups = useMemo(() => groupsOf(cards), [cards]);
  const pieces = cards.reduce((t0, c) => t0 + cardPieces(c), 0);
  const totals = useMemo(() => groups.map((g) => groupTotals(cards, g.cards, conds[g.key])), [groups, cards, conds]);
  const net = totals.reduce((t0, x) => t0 + x.netCents, 0);
  const today = todayCivil();
  const taken = useMemo(() => new Set(takenNumbers), [takenNumbers]);
  // ⚠️ LES NUMÉROS SUIVENT LES FOURNISSEURS QUI PARTENT — la même règle que le lot envoyé, sinon l'écran
  //    montrerait un numéro et en enregistrerait un autre.
  const numbers = useMemo(() => numbersOf(filledGroups(cards), conds, taken, today, kind), [cards, conds, taken, today, kind]);
  const filledCount = numbers.size;
  const title = isBl ? t('Nouveau bon de livraison')
    : loadedDrafts.length ? t('Brouillon {numbers}', { numbers: loadedDrafts.map((d) => d.number).join(', ') }) : t('Nouvelle commande fournisseur');

  useEffect(() => {
    if (isBl) return undefined;
    let alive = true;
    void api.mailReadiness().then((r) => { if (alive) setMail(r); }).catch(() => { /* on ne sait pas : rien ne s'annonce envoyé */ });
    return () => { alive = false; };
  }, [isBl]);
  const plan = sendPlan(cards, mail?.ready === true);

  const updateCard = (i: number, next: Partial<EditorCard>) => setCards((cs) => cs.map((c, k) => (k === i ? { ...c, ...next } : c)));
  const updateCond = (key: string, next: Partial<EditorConditions>) =>
    setConds((c) => ({ ...c, [key]: { ...(c[key] ?? conditionsFromTerms(null)), ...next } }));
  const hasOrphan = groups.some((g) => g.orphanBrand);

  // ── Les fournisseurs, pour rattacher une marque ───────────────────────────────────────────────
  useEffect(() => {
    if (!hasOrphan || suppliers !== null) return;
    let alive = true;
    api.referentielSuppliers()
      .then((r) => { if (alive) setSuppliers(r.rows.map((x) => ({ code: x.code, label: x.label }))); })
      .catch((e) => { if (alive) setSuppliersErr(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; };
  }, [hasOrphan, suppliers]);

  /**
   * RATTACHE UNE MARQUE À UN FOURNISSEUR — dans les référentiels, une fois pour toutes, par la même écriture
   * que l'écran Fournisseurs & marques ; puis relit ses modèles.
   * ⚠️ LE FOURNISSEUR SE DÉDUIT, il ne se recopie pas : c'est la relecture qui dit où tombent les articles, et
   *    une écriture refusée par le producteur ne déplace rien à l'écran.
   */
  const attach = useCallback(async (brand: OrderBrand, supplierCode: string) => {
    setSaveErr(null);
    try {
      await api.createReferentielEntry('brand', { code: brand.code, supplierCode }, `attach:brand:${brand.code}`);
      const keys = cards.filter((c) => c.model.brandRef?.id === brand.id).map((c) => c.model.modelKey);
      const fresh = (await api.orderEditorModels(keys)).rows;
      const sup = fresh.find((m) => m.supplier)?.supplier ?? null;
      if (!sup) {
        setSaveErr(t('La marque {brand} est rattachée, mais ses articles ne mènent toujours à aucun fournisseur actif.', { brand: brand.label }));
        return;
      }
      const orphanKey = `brand:${brand.id}`;
      setCards((cs) => cs.map((c) => {
        const m = fresh.find((x) => x.modelKey === c.model.modelKey);
        return m ? { ...c, model: m } : c;
      }));
      setConds((c) => {
        const orphan = c[orphanKey];
        const next = { ...c };
        delete next[orphanKey];
        // ⚠️ LE BROUILLON DE LA MARQUE PASSE AU FOURNISSEUR, avec son numéro et sa version : c'est le même
        //    document, désormais adressé. Un fournisseur déjà dans la saisie garde ses propres conditions.
        if (!next[sup.id]) {
          next[sup.id] = conditionsFromTerms(sup.terms, orphan?.draftNumber ?? null, orphan?.draftUpdatedAt ?? null, orphan?.orderedOn);
        }
        return next;
      });
      showToast(t('Marque {brand} rattachée à {supplier} — enregistré dans les référentiels', { brand: brand.label, supplier: sup.label }), 'ok');
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : String(e));
    }
  }, [cards]);

  /**
   * CRÉE un fournisseur depuis le sélecteur — par le même producteur que les Référentiels — et rend son code.
   * ⚠️ LE CODE EST CELUI QUE LE PRODUCTEUR A RETENU : on relit la liste, on ne le devine pas.
   */
  const createSupplier = useCallback(async (label: string): Promise<string> => {
    // ⚠️ CRÉER SEULEMENT : un fournisseur dont le code normalisé existe déjà est REFUSÉ (le serveur nomme la ligne) —
    //    l'écriture ordinaire le réécrivait en silence, code et libellé compris.
    await api.createMasterDataOnly('supplier', { code: label, label }, `ref:supplier:${label}`);
    const r = await api.referentielSuppliers();
    setSuppliers(r.rows.map((x) => ({ code: x.code, label: x.label })));
    const made = r.rows.find((x) => x.label.trim().toLowerCase() === label.trim().toLowerCase());
    if (!made) throw new Error(t('Le fournisseur « {label} » n’a pas été retrouvé après sa création.', { label }));
    return made.code;
  }, []);

  // ── Enregistrer ───────────────────────────────────────────────────────────────────────────────
  /** Ce qui empêche d'enregistrer, dit à l'opérateur — le document et le champ à corriger. */
  const problemText = (p: BatchProblem): string => (p.kind === 'price' ? t('Prix d’achat illisible sur « {model} » : quatre décimales au plus.', { model: p.model })
    : p.kind === 'number' ? (isBl ? t('BL {supplier} : {why}', { supplier: p.supplier, why: numberProblemText(p.problem, kind) })
      : t('Commande {supplier} : {why}', { supplier: p.supplier, why: numberProblemText(p.problem, kind) }))
      : p.kind === 'date' ? (isBl ? t('BL {supplier} : {why}', { supplier: p.supplier, why: dateProblemText(p.problem) })
        : t('Commande {supplier} : {why}', { supplier: p.supplier, why: dateProblemText(p.problem) }))
        : t('{supplier} : {field} ne se lit pas — corrigez-la dans le panneau de droite.', { supplier: p.supplier, field: conditionField(p.field) }));

  /**
   * Ce qu'un enregistrement partiel laisse dans l'éditeur : les marques sans fournisseur, le reste est parti.
   * ⚠️ CE QUI RESTE DEVIENT UN GESTE NEUF — sous l'ancienne clé, rattacher la marque à un fournisseur déjà servi par
   *    ce geste passerait pour un rejeu. La clé neuve dérive de l'ancienne : deux enregistrements dans la même
   *    milliseconde restent deux gestes.
   */
  const keepHeld = (heldText: string, numbersSaved: string[]) => {
    const sent = new Set(groups.filter((g) => !g.orphanBrand).map((g) => g.key));
    const keptDrafts = new Set(groups.filter((g) => g.orphanBrand).map((g) => conds[g.key]?.draftNumber).filter(Boolean));
    setCards((cs) => cs.filter((c) => !sent.has(groupKeyOf(c.model) ?? '')));
    setConds((c) => Object.fromEntries(Object.entries(c).filter(([k]) => !sent.has(k))));
    setLinks((l) => Object.fromEntries(Object.entries(l).filter(([k]) => !sent.has(k))));
    setLoadedDrafts((ds) => ds.filter((d) => keptDrafts.has(d.number)));
    gestureKey.current = cleDeGeste(`${gestureKey.current}:suite`, Date.now());
    setSaveErr(heldText);
    setSaving(false);
    onSaved(numbersSaved, true);
  };

  /**
   * ENREGISTRE LES BL — à intégrer depuis la liste (`save`), ou intégrés tout de suite, après confirmation.
   * ⚠️ L'INTÉGRATION SE CONFIRME : un BL intégré ne se modifie plus, une erreur se corrige ensuite par une correction
   *    de stock — l'opérateur doit le lire avant, pas le découvrir après.
   */
  const saveDeliveries = async (mode: 'save' | 'integrate') => {
    const b = deliveryBatchOf(cards, conds, links, taken, today, mode);
    const p = b.problems[0];
    if (p) { setSaveErr(problemText(p)); return; }
    const heldText = b.held.length
      ? t('{brands} : rattachez la marque à un fournisseur pour intégrer ses pièces.', { brands: b.held.join(', ') }) : null;
    if (b.groups.length === 0) { setSaveErr(heldText); return; }
    const piecesOf = (g: (typeof b.groups)[number]) => g.lines.reduce((n, l) => n + l.qty, 0);
    if (mode === 'integrate') {
      const total = b.groups.reduce((n, g) => n + piecesOf(g), 0);
      const dates = [...new Set(b.groups.map((g) => new Date(g.deliveredOn).toLocaleDateString()))].join(', ');
      const ok = await confirm({
        title: t('Intégrer {n} pièce en stock ?', { n: total }),
        message: t('{list} — entrée en stock datée du {dates}. Un BL intégré ne se modifie plus : une erreur se corrige ensuite par une correction de stock.', {
          list: b.groups.map((g) => t('{number} ({pieces})', { number: g.number, pieces: t('{n} pièce', { n: piecesOf(g) }) })).join(', '), dates,
        }),
        confirmLabel: t('Intégrer en stock'), cancelLabel: t('Revenir'), tone: 'info',
      });
      if (!ok) return;
    }
    setSaving(true);
    setSaveErr(null);
    try {
      const r = await api.saveDeliveryBatch({ key: gestureKey.current, mode, groups: b.groups });
      const list = r.rows.map((x) => t('{number} ({supplier} · {pieces})', { number: x.number, supplier: x.supplierLabel, pieces: t('{n} pièce', { n: x.pieces }) })).join(', ');
      if (mode === 'save') {
        showToast(t('{n} BL enregistré — {list}. À intégrer depuis la liste des réceptions.', { n: r.rows.length, list }), 'ok');
      } else {
        showToast(t('{n} pièce entrée en stock — {list}', { n: r.rows.reduce((n, x) => n + x.pieces, 0), list }), 'ok');
        // ⚠️ CE QUI EST ENTRÉ SANS PRIX SE DIT — rien n'a été bloqué, rien n'est caché non plus.
        const unpriced = r.rows.reduce((n, x) => n + x.piecesWithoutPrice, 0);
        if (unpriced > 0) showToast(t('{n} pièce entrée sans prix d’achat, à valoriser.', { n: unpriced }), 'warn');
        const restored = r.rows.reduce((n, x) => n + x.restoredModels, 0);
        if (restored > 0) showToast(t('{n} modèle archivé remis en service : de la marchandise vient d’entrer pour lui.', { n: restored }));
      }
      if (heldText) { keepHeld(heldText, r.rows.map((x) => x.number)); return; }
      onSaved(r.rows.map((x) => x.number), false);
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  };

  const save = async (mode: 'draft' | 'validate') => {
    if (saving) return;
    if (isBl) { await saveDeliveries(mode === 'draft' ? 'save' : 'integrate'); return; }
    const b = batchOf(cards, conds, loadedDrafts, taken, today, mode);
    const p = b.problems[0];
    if (p) { setSaveErr(problemText(p)); return; }
    const heldText = b.held.length
      ? t('{brands} : rattachez la marque à un fournisseur pour envoyer sa commande.', { brands: b.held.join(', ') }) : null;
    // ⚠️ RIEN À ENVOYER — saisie vide, ou seulement des marques sans fournisseur : on le dit, on n'appelle pas.
    if (b.groups.length === 0) { setSaveErr(heldText); return; }
    setSaving(true);
    setSaveErr(null);
    try {
      const r = await api.saveOrderBatch({ key: gestureKey.current, mode, groups: b.groups, discardDrafts: b.discardDrafts });
      const listOf = (rows: readonly { number: string; supplierLabel: string; pieces: number }[]) => rows.map((x) => t('{number} ({supplier} · {pieces})', { number: x.number, supplier: x.supplierLabel, pieces: t('{n} pièce', { n: x.pieces }) })).join(', ');
      if (mode === 'draft') showToast(t('Brouillon enregistré — {list}. Cliquez-le dans la liste pour le reprendre.', { list: listOf(r.rows) }), 'ok');
      else await transmit(r.rows, b.groups, listOf);
      if (heldText) { keepHeld(heldText, r.rows.map((x) => x.number)); return; }
      onSaved(r.rows.map((x) => x.number), false);
    } catch (e) {
      // ⚠️ Le refus du serveur reste DANS l'éditeur, avec son motif réel : la saisie n'est pas perdue.
      setSaveErr(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  };

  /**
   * APRÈS LA VALIDATION, L'ENVOI — chaque commande dont la fiche porte une adresse part par la messagerie du magasin ;
   * les autres sont à transmettre, et le dialogue propose leur PDF.
   * ⚠️ UN ENVOI REFUSÉ NE DÉFAIT PAS LA VALIDATION : la commande est enregistrée, elle reste « à transmettre », et le
   *    refus se dit avec son motif réel.
   * ⚠️ LES LIGNES RENDUES SUIVENT L'ORDRE DES GROUPES ENVOYÉS — c'est ce qui relie une commande à son fournisseur.
   */
  const transmit = async (
    rows: { number: string; supplierLabel: string; pieces: number }[], sentGroups: readonly OrderGroupInput[],
    listOf: (rows: readonly { number: string; supplierLabel: string; pieces: number }[]) => string,
  ) => {
    const canMail = mail?.ready === true;
    const sent: typeof rows = [];
    /** Ce qui ne partira pas par e-mail : fiche sans adresse, ou messagerie du magasin non réglée. */
    const toAsk: typeof rows = [];
    for (const [i, row] of rows.entries()) {
      const email = canMail ? orderEmailOf(cards, sentGroups[i]?.supplierId ?? '') : null;
      if (!email) { toAsk.push(row); continue; }
      try {
        await api.sendOrder(row.number);
        sent.push(row);
      } catch (e) {
        showToast(t('Commande {number} validée, mais pas envoyée : {why} Elle reste à transmettre.', { number: row.number, why: e instanceof Error ? e.message : String(e) }), 'err', 9000);
      }
    }
    showToast(sent.length === rows.length
      ? t('{n} commande validée et envoyée — {list}', { n: rows.length, list: listOf(rows) })
      : t('{n} commande validée — {list}', { n: rows.length, list: listOf(rows) }), 'ok');
    if (sent.length && sent.length < rows.length) showToast(t('{n} bon de commande envoyé par e-mail — {list}', { n: sent.length, list: listOf(sent) }), 'ok');
    if (toAsk.length === 0) return;
    const list = toAsk.map((x) => `${x.number} — ${x.supplierLabel}`).join(', ');
    // ⚠️ LE DIALOGUE N'ATTEND PAS : la saisie est enregistrée, l'éditeur peut se fermer pendant qu'on lit.
    void confirm({
      title: t('{n} commande à transmettre vous-même', { n: toAsk.length }),
      message: canMail
        ? t('{list} : pas d’e-mail sur la fiche fournisseur. La commande est validée, à transmettre : téléchargez son PDF pour la transmettre. Ajoutez l’e-mail dans Référentiels → Fournisseurs : la prochaine partira seule.', { list })
        : t('{list} : l’envoi par e-mail n’est pas possible — {why} La commande est validée, à transmettre : téléchargez son PDF pour la transmettre.', { list, why: mail?.why || t('La messagerie du magasin n’a pas pu être lue.') }),
      confirmLabel: t('Télécharger le PDF'), cancelLabel: t('Fermer'), tone: 'info',
    }).then((ok) => { if (ok) for (const x of toAsk) downloadOrderPdf(x.number); });
  };

  // ── Fermer ────────────────────────────────────────────────────────────────────────────────────
  const requestClose = useCallback(async () => {
    // ⚠️ PAS DE FERMETURE PENDANT L'ENREGISTREMENT : un refus arrivé après la fermeture n'aurait plus d'écran
    //    où se dire, et la saisie serait perdue sans un mot.
    if (saving) return;
    if (cards.length > 0) {
      const ok = await confirm({
        title: t('Fermer sans enregistrer ?'),
        message: t('La saisie en cours sera perdue. Enregistrez-la en brouillon pour la reprendre plus tard.'),
        confirmLabel: t('Fermer'), cancelLabel: t('Continuer la saisie'),
      });
      if (!ok) return;
    }
    onClose();
  }, [cards.length, saving, onClose]);
  const { panelRef } = useOverlayPanel({ open: true, onClose: () => void requestClose(), autoFocus: 'panel', blockClose: () => saving });

  useEffect(() => { if (!draftNumber) setTimeout(() => searchRef.current?.focus(), 60); }, [draftNumber]);

  const btn = (on: boolean, primary: boolean): CSSProperties => ({
    height: 38, padding: primary ? '0 17px' : '0 15px', borderRadius: 'var(--r-ctrl)', fontSize: '13px', fontWeight: 700,
    whiteSpace: 'nowrap', cursor: on ? 'pointer' : 'not-allowed',
    border: primary ? 'none' : '1px solid var(--ink-300)',
    background: primary ? (on ? 'var(--teal-700)' : 'var(--ink-200)') : 'var(--paper)',
    color: primary ? (on ? '#fff' : 'var(--ink-400)') : 'var(--ink-800)', opacity: !primary && !on ? 0.5 : 1,
  });
  const ready = pieces > 0 && !saving;

  // ⚠️ LE VOILE EST CELUI DE LA MODALE PARTAGÉE (`ModalBackdrop`) : seule la taille du panneau diffère —
  //    `Modal` plafonne à 920 px, le dessin veut 90 % de l'écran. Pas de fermeture au clic sur le voile :
  //    c'est un panneau de travail, ce clic n'y est jamais voulu.
  return (
    <ModalBackdrop>
        <div ref={panelRef} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} style={{
          position: 'relative', width: '90vw', maxWidth: 'min(1680px, 100%)', height: '90vh', maxHeight: '100%',
          background: 'var(--paper)', borderRadius: 'var(--r-lg)', boxShadow: 'var(--sh-modal)', display: 'flex',
          flexDirection: 'column', overflow: 'hidden',
        }}>
          {/* En-tête : titre, état, consigne, fermeture. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 22px', borderBottom: '1px solid var(--ink-200)', flexShrink: 0, flexWrap: 'wrap' }}>
            <span style={{ width: 38, height: 38, borderRadius: 'var(--r-sm)', background: 'var(--ink-900)', color: 'var(--paper)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden><rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 4V3h6v1M9 10h6M9 14h6M9 18h3" /></svg>
            </span>
            <span style={{ flex: 1, minWidth: 240 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '17px', fontWeight: 700, color: 'var(--ink-900)' }}>{title}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px', borderRadius: 'var(--r-pill)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: '11px', fontWeight: 700 }}>
                  {isBl ? t('Nouveau') : loadedDrafts.length ? t('Brouillon') : t('Nouvelle')}</span>
              </span>
              <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 2 }}>
                {isBl ? t('Cherchez ou bipez un article, puis saisissez les quantités reçues. Un BL par fournisseur est créé.')
                  : t('Cherchez ou bipez un article, puis remplissez sa grille coloris × taille. Une commande par fournisseur est créée.')}</span>
            </span>
            <button type="button" onClick={() => void requestClose()} aria-label={t('Fermer')} style={{ width: 34, height: 34, border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', color: 'var(--ink-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0, cursor: 'pointer' }}>
              <Icon name="x" size={15} stroke={2.4} />
            </button>
          </div>

          <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 340px' }}>
            <div style={{ minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--ink-200)' }}>
              {/* La recherche — le champ de scan de la charte. */}
              <div style={{ padding: '14px 22px', borderBottom: '1px solid var(--ink-100)', position: 'relative', flexShrink: 0, zIndex: 5 }}>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 11, padding: '10px 13px', borderRadius: 'var(--r-sm)', background: 'var(--teal-100)', border: '1.5px solid var(--teal-400)', flexWrap: 'wrap' }}>
                  <span style={{ display: 'flex', color: 'var(--teal-800)', flexShrink: 0 }}>
                    <Icon name="barcode" size={19} stroke={2} />
                  </span>
                  <input ref={searchRef} value={q} onChange={(e) => { setQ(e.target.value); setSearchErr(null); setFlash(null); }} onKeyDown={(e) => void onSearchKey(e)}
                    autoComplete="off" placeholder={t('Bipez, ou tapez un nom / code / gencod')} aria-label={t('Chercher ou biper un article')}
                    style={{ flex: 1, minWidth: 230, height: 38, padding: '0 12px', border: '1px solid var(--teal-400)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '13px', outline: 'none', fontFamily: MONO }} />
                  <span style={{ fontSize: '11.5px', color: 'var(--teal-800)', fontWeight: 600, whiteSpace: 'nowrap' }}>{t('Entrée ↵ ajoute l’article')}</span>
                </div>
                {hits !== null && (
                  <div onMouseDown={(e) => e.preventDefault()} style={{ position: 'absolute', left: 22, right: 22, top: 'calc(100% - 8px)', background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', boxShadow: '0 16px 38px -10px rgba(12,14,16,.3)', maxHeight: 380, overflowY: 'auto', zIndex: 20 }}>
                    {hits.map((r, i) => (
                      <button key={r.modelKey} type="button" onClick={() => void addModel(r.modelKey, null)}
                        style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '9px 14px', border: 'none', borderBottom: '1px solid var(--ink-100)', background: i === hi ? 'var(--teal-100)' : 'transparent', textAlign: 'left', cursor: 'pointer' }}>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</span>
                          <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--ink-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <span style={{ fontFamily: MONO }}>{r.articleCode ?? r.modelKey}</span>
                            {' · '}{[r.family, r.supplierLabel ?? (r.brandLabel ? t('{brand} — fournisseur à définir', { brand: r.brandLabel }) : t('sans fournisseur ni marque')), t('{colors} × {sizes}', { colors: t('{n} coloris', { n: r.colors }), sizes: t('{n} taille', { n: r.sizes }) })].filter(Boolean).join(' · ')}
                          </span>
                        </span>
                        {cards.some((c) => c.model.modelKey === r.modelKey) && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 8px', borderRadius: 'var(--r-pill)', background: 'var(--teal-100)', color: 'var(--teal-800)', fontSize: '10.5px', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}>{isBl ? t('Déjà dans le BL') : t('Déjà dans la commande')}</span>
                        )}
                        <span style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--ink-800)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                          {(() => {
                            // ⚠️ Le prix montré est celui que la carte recevra : la fiche pour un BL, la dernière commande sinon.
                            const fine = isBl ? r.sheetPrice : r.lastPrice?.unitPriceFine ?? null;
                            return fine != null ? t('{v} HT', { v: euros(Math.round(fine / 100)) }) : '—';
                          })()}</span>
                        <span aria-hidden style={{ width: 28, height: 28, borderRadius: 'var(--r-xs)', background: 'var(--ink-900)', color: 'var(--paper)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Icon name="plus" size={13} stroke={2.6} />
                        </span>
                      </button>
                    ))}
                    {hits.length === 0 && <div style={{ padding: 14, textAlign: 'center', fontSize: '12.5px', color: 'var(--ink-400)' }}>{t('Aucun article ne correspond.')}</div>}
                    <div style={{ padding: '7px 14px', background: 'var(--ink-50)', fontSize: '11px', color: 'var(--ink-500)' }}>
                      {t('↑ ↓ pour choisir · Entrée ajoute l’article et place le curseur dans sa grille · Échap efface')}</div>
                  </div>
                )}
                {flash && !searchErr && (
                  <div role="status" style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 7, fontSize: '12.5px', fontWeight: 600, color: 'var(--teal-800)' }}>
                    <Icon name="check" size={14} stroke={2.4} />{flash}</div>
                )}
                {searchErr && <div role="alert" style={{ marginTop: 8, fontSize: '12.5px', fontWeight: 600, color: 'var(--rose-fg)' }}>{searchErr}</div>}
              </div>

              {/* Les articles, rangés par fournisseur. */}
              <div ref={listRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px 22px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>
                {notice && <div role="status" style={{ padding: '9px 12px', borderRadius: 'var(--r-sm)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: '12.5px', fontWeight: 600 }}>{notice}</div>}
                {loading && <div style={{ fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Lecture du brouillon…')}</div>}
                {!loading && cards.length === 0 && (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '40px 20px', textAlign: 'center' }}>
                    <span style={{ width: 48, height: 48, borderRadius: 'var(--r-md)', background: 'var(--ink-100)', color: 'var(--ink-500)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon name="box" size={22} stroke={1.8} />
                    </span>
                    <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Aucun article pour l’instant')}</span>
                    <span style={{ maxWidth: 420, fontSize: '13px', color: 'var(--ink-500)', lineHeight: 1.5 }}>
                      {isBl ? t('Cherchez un article ci-dessus — ou partez d’une commande ouverte, dans le panneau de droite.')
                        : t('Cherchez un article ci-dessus — ou partez d’une commande existante ou d’un réassort sur ventes, dans le panneau de droite.')}</span>
                  </div>
                )}
                {groups.map((g, gi) => (
                  <div key={g.key} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', height: 24, padding: '0 10px', borderRadius: 'var(--r-pill)', background: g.orphanBrand ? 'var(--amber-bg)' : 'var(--ink-900)', color: g.orphanBrand ? 'var(--amber-fg)' : 'var(--paper)', fontSize: '11.5px', fontWeight: 700, letterSpacing: '.03em', whiteSpace: 'nowrap' }}>
                        {g.orphanBrand ? t('{brand} — fournisseur à définir', { brand: g.supplierLabel }) : g.supplierLabel}</span>
                      <span style={{ fontFamily: MONO, fontSize: '11.5px', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>
                        {numbers.get(g.key)?.number ?? ''}</span>
                      <span style={{ flex: 1, height: 1, background: 'var(--ink-100)' }} />
                      <span style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--ink-800)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {t('{pieces} · {net} HT net', { pieces: t('{n} pièce', { n: totals[gi]!.pieces }), net: euros(totals[gi]!.netCents) })}</span>
                    </div>
                    {g.cards.map((i) => (
                      <ModelCard key={cards[i]!.model.modelKey} card={cards[i]!}
                        onChange={(next) => updateCard(i, next)}
                        onRemove={() => setCards((cs) => cs.filter((_, k) => k !== i))}
                        onLastCell={() => searchRef.current?.focus()} kind={kind} />
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Le panneau des conditions et « Partir de… ». */}
            <div style={{ minWidth: 0, minHeight: 0, overflowY: 'auto', background: 'var(--ink-50)', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Eyebrow>{isBl ? t('Par fournisseur') : t('Conditions par fournisseur')}</Eyebrow>
              {groups.length === 0 && (
                <p style={{ margin: 0, fontSize: '12.5px', color: 'var(--ink-500)', lineHeight: 1.5 }}>
                  {isBl ? t('N° du BL, date du BL et commande rattachée apparaissent ici dès le premier article.')
                    : t('N° de commande, date de commande, remise et port apparaissent ici dès le premier article.')}</p>
              )}
              {groups.map((g, gi) => (g.orphanBrand ? (
                <OrphanCard key={g.key} kind={kind} brand={g.orphanBrand} num={numbers.get(g.key) ?? null}
                  suppliers={suppliers} suppliersErr={suppliersErr}
                  onNumber={(number) => updateCond(g.key, { number })}
                  onAttach={(code) => attach(g.orphanBrand!, code)} onCreateSupplier={createSupplier} />
              ) : isBl ? (
                <DeliveryCard key={g.key} label={g.supplierLabel} num={numbers.get(g.key) ?? null} today={today}
                  value={conds[g.key] ?? conditionsFromTerms(null)} link={links[g.key] ?? null} totals={totals[gi]!}
                  onChange={(next) => updateCond(g.key, next)}
                  onUnlink={() => setLinks((l) => Object.fromEntries(Object.entries(l).filter(([k]) => k !== g.key)))} />
              ) : (
                <TermsCard key={g.key} label={g.supplierLabel} num={numbers.get(g.key) ?? null} today={today}
                  value={conds[g.key] ?? conditionsFromTerms(null)} totals={totals[gi]!}
                  mail={mailLine(orderEmailOf(cards, g.key), mail)}
                  onChange={(next) => updateCond(g.key, next)} />
              )))}
              <Eyebrow style={{ marginTop: 6 }}>{t('Partir de…')}</Eyebrow>
              {isBl && <>
              <StartTool on={tool === 'dup'} label={t('Une commande ouverte')} desc={t('Reprendre son reste à livrer et y rattacher le BL')}
                icon="M6 2h9l5 5v15H6zM14 2v6h6M9 13h6M9 17h6" onClick={() => setTool((x) => (x === 'dup' ? '' : 'dup'))} />
              {tool === 'dup' && <OpenOrderTool onApply={(incoming, order, pieces, skipped) => {
                for (const m of incoming) {
                  const key = groupKeyOf(m.model);
                  if (key) setConds((c) => (c[key] ? c : { ...c, [key]: conditionsFromTerms(null) }));
                }
                setCards((cs) => mergeQuantities(cs, incoming, 'add', kind));
                setTool('');
                // ⚠️ LE BL SE RATTACHE SOUS LE FOURNISSEUR DE LA COMMANDE : si ses articles relèvent aujourd'hui d'un autre,
                //    aucun groupe ne le porterait — on le dit plutôt que de garder un rattachement que rien n'affiche.
                const linked = !!order.supplierId && incoming.some((m) => groupKeyOf(m.model) === order.supplierId);
                if (linked) setLinks((l) => ({ ...l, [order.supplierId!]: order.number }));
                const piecesText = t('{n} pièce', { n: pieces });
                showToast(skipped > 0
                  ? t('Reste à livrer de {number} ajouté — {pieces}, {skipped}. Ajustez au réel reçu.', {
                    number: order.number, pieces: piecesText, skipped: t('{n} ligne ignorée (article hors catalogue, ou ni fournisseur ni marque)', { n: skipped }),
                  })
                  : t('Reste à livrer de {number} ajouté — {pieces}. Ajustez au réel reçu.', { number: order.number, pieces: piecesText }), 'ok');
                if (!linked) showToast(t('Les articles de {number} relèvent aujourd’hui d’un autre fournisseur : le BL ne lui est pas rattaché.', { number: order.number }), 'warn');
              }} />}
              </>}
              {!isBl && <>
              <StartTool on={tool === 'dup'} label={t('Dupliquer une commande')} desc={t('Reprendre les lignes d’une commande existante')}
                icon="M8 8h11v11H8zM5 16V5h11" onClick={() => setTool((x) => (x === 'dup' ? '' : 'dup'))} />
              {tool === 'dup' && <DuplicateOrder onApply={(incoming, label, skipped) => {
                for (const m of incoming) {
                  const sup = m.model.supplier;
                  if (sup) setConds((c) => (c[sup.id] ? c : { ...c, [sup.id]: conditionsFromTerms(sup.terms) }));
                }
                setCards((cs) => mergeQuantities(cs, incoming, 'add'));
                setTool('');
                const lines = incoming.reduce((n, x) => n + Object.keys(x.qty).length, 0);
                showToast(skipped > 0
                  ? t('{label} dupliquée — {lines} ajoutées à la saisie, {skipped}', { label, lines: t('{n} ligne', { n: lines }), skipped: t('{n} ligne ignorée (article hors catalogue ou sans fournisseur)', { n: skipped }) })
                  : t('{label} dupliquée — {lines} ajoutées à la saisie', { label, lines: t('{n} ligne', { n: lines }) }), 'ok');
              }} />}
              <StartTool on={tool === 'reas'} label={t('Réassort sur ventes')} desc={t('Proposer les quantités vendues sur une période')}
                icon="M3 17l5-5 4 4 8-8M15 8h5v5" onClick={() => setTool((x) => (x === 'reas' ? '' : 'reas'))} />
              {tool === 'reas' && <RestockFromSales onApply={(incoming, label) => {
                for (const m of incoming) {
                  const sup = m.model.supplier;
                  if (sup) setConds((c) => (c[sup.id] ? c : { ...c, [sup.id]: conditionsFromTerms(sup.terms) }));
                }
                setCards((cs) => mergeQuantities(cs, incoming, 'set'));
                setTool('');
                showToast(t('Réassort {label} — {n} article proposé, à ajuster dans les grilles', { label, n: incoming.length }), 'ok');
              }} />}
              </>}
            </div>
          </div>

          {/* Le pied : annuler, le récapitulatif, enregistrer. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 22px', borderTop: '1px solid var(--ink-100)', background: 'var(--ink-50)', flexShrink: 0, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => void requestClose()} style={{ height: 38, padding: '0 13px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'transparent', color: 'var(--ink-600)', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>{t('Annuler')}</button>
            <span style={{ flex: 1, minWidth: 200, textAlign: 'right', fontSize: '12.5px', color: saveErr ? 'var(--rose-fg)' : 'var(--ink-600)', fontWeight: saveErr ? 600 : 400, fontVariantNumeric: 'tabular-nums' }} role={saveErr ? 'alert' : undefined}>
              {saveErr ?? (cards.length ? [
                t('{n} fournisseur', { n: groups.length }), t('{n} article', { n: cards.length }), t('{n} pièce', { n: pieces }), t('{v} HT net', { v: euros(net) }),
              ].join(' · ') : '')}
            </span>
            <button type="button" onClick={() => void save('draft')} aria-disabled={!ready || undefined} style={btn(ready, false)}>{t('Enregistrer le brouillon')}</button>
            <button type="button" onClick={() => void save('validate')} aria-disabled={!ready || undefined} style={btn(ready, true)}>
              {saving ? t('Enregistrement…') : isBl ? (filledCount > 1 ? t('Intégrer {n} BL en stock', { n: filledCount }) : t('Intégrer en stock'))
                : validateLabel(plan, filledCount)}</button>
          </div>
        </div>
    </ModalBackdrop>
  );
}

/**
 * LE BOUTON DE VALIDATION dit ce qui va se passer : « envoyer » seulement pour ce qui partira par e-mail.
 * ⚠️ SI UNE PARTIE SEULEMENT PART, LE BOUTON LE CHIFFRE : « Valider et envoyer » pour trois commandes dont une sans
 *    adresse annoncerait un envoi qui n'aura pas lieu.
 */
function validateLabel(plan: { valid: number; sendable: number }, filled: number): string {
  if (plan.sendable > 0 && plan.sendable === plan.valid) {
    return plan.valid > 1 ? t('Valider et envoyer {n} commandes', { n: plan.valid }) : t('Valider et envoyer');
  }
  if (plan.sendable > 0) return t('Valider {n} commandes, en envoyer {sent}', { n: plan.valid, sent: plan.sendable });
  return filled > 1 ? t('Valider les {n} commandes', { n: filled }) : t('Valider la commande');
}

/** La ligne d'envoi d'une commande dans le panneau : à qui elle partira, ou pourquoi elle ne partira pas. */
function mailLine(email: string | null, mail: { ready: boolean; why: string } | null): { text: string; warn: boolean } {
  if (!email) return { text: t('Aucun e-mail sur la fiche fournisseur'), warn: true };
  if (mail?.ready) return { text: t('Envoi à {email}', { email }), warn: false };
  // ⚠️ SANS MESSAGERIE RÉGLÉE (ou illisible), L'ADRESSE EXISTE MAIS RIEN NE PARTIRA : on ne dit pas « Envoi à ».
  return mail ? { text: t('{email} — messagerie du magasin non réglée, à transmettre vous-même', { email }), warn: true }
    : { text: t('E-mail de commande : {email}', { email }), warn: false };
}

/** Télécharge le bon de commande — le même octet que la pièce jointe de l'envoi. */
function downloadOrderPdf(number: string): void {
  const a = document.createElement('a');
  a.href = api.orderPdfUrl(number, true);
  a.download = '';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function Eyebrow({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <span style={{ fontFamily: MONO, fontSize: '10px', fontWeight: 600, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--ink-500)', ...style }}>{children}</span>;
}

/** Une carte d'article : son prix, ses gestes de grille, et la grille coloris × taille. */
function ModelCard({ card, onChange, onRemove, onLastCell, kind }: {
  card: EditorCard;
  onChange: (next: Partial<EditorCard>) => void;
  onRemove: () => void;
  onLastCell: () => void;
  kind: EditorKind;
}) {
  const grid = useMemo(() => gridOf(card.model), [card.model]);
  const m = card.model;
  const p = cardPieces(card);
  const fine = fineFromInput(card.pa);
  const amount = typeof fine === 'number' ? Math.round((p * fine) / 100) : null;
  const sizesLabel = grid.sizes.length > 1 ? `${grid.sizes[0]}–${grid.sizes[grid.sizes.length - 1]}` : (grid.sizes[0] ?? '—');
  const colors = new Set(grid.rows.map((r) => r.color)).size;
  const sub = [m.family, t('{n} coloris', { n: colors }), t('tailles {v}', { v: sizesLabel }),
    m.dimension ? `${m.dimension.label.toLowerCase()} ${m.dimension.values.join(' / ')}` : null].filter(Boolean).join(' · ');
  const small: CSSProperties = { height: 30, padding: '0 10px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer' };

  // ⚠️ LA CARTE EST UN GROUPE NOMMÉ PAR L'ARTICLE : son prix et son « Retirer » se répètent sur chaque carte, et seul le
  //    nom du groupe dit à une aide technique de quel article ils parlent.
  return (
    <div role="group" aria-label={m.name} style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: '1px solid var(--ink-100)', flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: 200 }}>
          <span style={{ display: 'block', fontSize: '14px', fontWeight: 700, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.name}</span>
          <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--ink-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <span style={{ fontFamily: MONO }}>{m.articleCode ?? m.modelKey}</span> · {sub}</span>
        </span>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--ink-600)' }}>{t('{v} HT', { v: fieldLabel('purchasePriceExclTax') })}</span>
          <input value={card.pa} onChange={(e) => onChange({ pa: e.target.value })} inputMode="decimal" aria-label={t('{v} HT', { v: fieldLabel('purchasePriceExclTax') })}
            title={priceHint(m, kind)}
            style={{ width: 80, height: 32, padding: '0 9px', border: `1px solid ${fine === undefined ? 'var(--rose-fg)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', fontSize: '13px', fontWeight: 700, textAlign: 'right', color: 'var(--ink-900)', outline: 'none', boxSizing: 'border-box', fontVariantNumeric: 'tabular-nums' }} />
          <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-400)' }}>{currentCurrency().symbol}</span>
        </label>
        <button type="button" onClick={() => onChange({ qty: fillAll(grid) })} title={t('Une pièce dans chaque case de la grille')} style={small}>{t('1 partout')}</button>
        <button type="button" onClick={() => onChange({ qty: {} })} title={t('Remettre la grille à zéro')} style={small}>{t('Vider')}</button>
        <span style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--ink-800)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {amount === null ? t('{n} pièce', { n: p }) : t('{pieces} · {v} HT', { pieces: t('{n} pièce', { n: p }), v: euros(amount) })}</span>
        <button type="button" onClick={onRemove} aria-label={t('Retirer l’article')} title={kind === 'order' ? t('Retirer l’article de la commande') : t('Retirer l’article du BL')}
          style={{ width: 30, height: 30, border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent', color: 'var(--rose-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, cursor: 'pointer' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 11v6M14 11v6" /></svg>
        </button>
      </div>
      <QtyGrid card={card} grid={grid} onQty={(qty) => onChange({ qty })} onLastCell={onLastCell} />
    </div>
  );
}

/** La grille coloris × taille d'un article — une case par déclinaison, Entrée passe à la suivante. */
function QtyGrid({ card, grid, onQty, onLastCell }: { card: EditorCard; grid: Grid; onQty: (qty: Record<string, number>) => void; onLastCell: () => void }) {
  const n = grid.sizes.length;
  const w = n * 54 + Math.max(0, n - 1) * 4;
  const d2 = grid.axisLabel !== null;
  const cols = `150px ${w}px 56px 30px`;
  const hc = `repeat(${n}, 54px)`;
  const head: CSSProperties = { fontFamily: MONO, fontSize: '9.5px', letterSpacing: '.06em', textTransform: 'uppercase' };
  const colTot = columnTotals(card, grid);
  const grand = cardPieces(card);

  const set = (gencode: string, v: string) => {
    const q = readQty(v);
    if (q === null) return;
    const next = { ...card.qty };
    if (q > 0) next[gencode] = q; else delete next[gencode];
    onQty(next);
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const all = Array.from(e.currentTarget.closest('[data-grid]')?.querySelectorAll<HTMLInputElement>('input[data-cell]') ?? []);
    const k = all.indexOf(e.currentTarget);
    const next = all[k + 1];
    if (next) { next.focus(); next.select(); } else onLastCell();
  };
  const duplicate = async (rowIndex: number) => {
    const d = duplicateRow(card, grid, rowIndex);
    const src = grid.rows[rowIndex]!;
    const name = src.size2 ? `${src.color} ${src.size2}` : src.color;
    if (d.overwritten > 0) {
      const ok = await confirm({
        title: t('Remplacer les quantités déjà saisies ?'),
        message: t('{n} case des autres coloris a déjà une quantité différente. Elle prendra celle de {name}.', { n: d.overwritten, name }),
        confirmLabel: t('Dupliquer'), cancelLabel: t('Annuler'),
      });
      if (!ok) return;
    }
    onQty(d.qty);
    showToast(t('{name} dupliqué vers {targets}', { name, targets: t('{n} coloris', { n: d.targets }) }), 'ok');
  };

  return (
    <div style={{ overflowX: 'auto', padding: '12px 14px 14px' }} data-grid="">
      <div style={{ display: 'inline-grid', gridTemplateColumns: cols, gap: 4, alignItems: 'center', minWidth: 'max-content' }}>
        <span style={{ ...head, alignSelf: 'end', padding: '0 8px 3px 0', color: d2 ? 'var(--violet-fg)' : 'var(--ink-400)' }}>
          {d2 ? t('{label} ↓', { label: grid.axisLabel! }) : fieldLabel('color')}</span>
        <span style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          {d2 && <span style={{ ...head, color: 'var(--ink-500)', textAlign: 'center', paddingBottom: 2 }}>{fieldLabel('size')} →</span>}
          <span style={{ display: 'grid', gridTemplateColumns: hc, gap: 4 }}>
            {grid.sizes.map((s) => (
              <span key={s} style={{ height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--r-xs)', background: 'var(--ink-100)', fontFamily: MONO, fontSize: '11px', fontWeight: 700, color: 'var(--ink-700)' }}>{s || '—'}</span>
            ))}
          </span>
        </span>
        <span style={{ ...head, alignSelf: 'end', textAlign: 'right', paddingBottom: 5, color: 'var(--ink-400)' }}>{t('Total')}</span>
        <span />
        {grid.rows.map((row, ri) => {
          const tot = rowTotal(card, row);
          const others = new Set(grid.rows.filter((r) => r.color !== row.color).map((r) => r.color)).size;
          const canDup = tot > 0 && others > 0;
          const name = row.size2 ? `${row.color} ${row.size2}` : row.color;
          const sep = d2 && row.first && ri > 0 ? '1px solid var(--ink-100)' : 'none';
          return (
            <span key={`${row.color}|${row.size2 ?? ''}`} style={{ display: 'contents' }}>
              <span style={{ height: 36, display: 'flex', alignItems: 'center', gap: 5, padding: '0 8px 0 0', minWidth: 0, borderTop: sep }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.first ? row.color : ''}</span>
                {row.size2 && <span style={{ height: 18, display: 'inline-flex', alignItems: 'center', padding: '0 6px', borderRadius: 'var(--r-pill)', background: 'var(--violet-bg)', color: 'var(--violet-fg)', fontFamily: MONO, fontSize: '10px', fontWeight: 700, flexShrink: 0 }}>{row.size2}</span>}
              </span>
              <span style={{ display: 'grid', gridTemplateColumns: hc, gap: 4 }}>
                {row.cells.map((g, j) => (g === null ? (
                  // ⚠️ « absente du catalogue, ou sans code-barres » et non « non créée chez le fournisseur » (le dessin) :
                  //    c'est notre catalogue qui ne l'a pas, et l'écran ne sait rien du fournisseur.
                  <span key={j} title={t('Déclinaison absente du catalogue, ou sans code-barres — elle ne se commande pas.')}
                    style={{ height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed var(--ink-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-300)', fontSize: '13px', fontWeight: 700, boxSizing: 'border-box' }}>·</span>
                ) : (
                  <input key={g} data-cell={`${card.model.modelKey}|${g}`} value={card.qty[g] ? String(card.qty[g]) : ''} placeholder="0" inputMode="numeric"
                    aria-label={t('Quantité {color} {size}', { color: name, size: grid.sizes[j] ?? '' })}
                    onChange={(e) => set(g, e.target.value)} onKeyDown={onKey} onFocus={(e) => e.currentTarget.select()}
                    style={{ width: 54, height: 36, padding: 0, border: `1px solid ${card.qty[g] ? 'var(--teal-500)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-ctrl)', background: card.qty[g] ? 'var(--teal-100)' : 'var(--paper)', fontSize: '14px', fontWeight: 700, textAlign: 'center', color: 'var(--ink-900)', outline: 'none', boxSizing: 'border-box', fontVariantNumeric: 'tabular-nums' }} />
                )))}
              </span>
              <span style={{ textAlign: 'right', fontSize: '13px', fontWeight: 700, color: 'var(--ink-800)', fontVariantNumeric: 'tabular-nums' }}>{tot ? tot : ''}</span>
              {canDup ? (
                <button type="button" onClick={() => void duplicate(ri)}
                  aria-label={t('Dupliquer {name} vers {targets}', { name, targets: t('{n} autre coloris', { n: others }) })}
                  title={t('Dupliquer {name} vers {targets}', { name, targets: t('{n} autre coloris', { n: others }) })}
                  style={{ width: 30, height: 30, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)', background: 'var(--paper)', color: 'var(--ink-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, cursor: 'pointer' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a1 1 0 0 1 1-1h10" /></svg>
                </button>
              ) : <span style={{ display: 'block', width: 30, height: 30 }} />}
            </span>
          );
        })}
        <span style={{ ...head, paddingTop: 6, borderTop: '1px solid var(--ink-100)', color: 'var(--ink-500)' }}>{t('Total')}</span>
        <span style={{ display: 'grid', gridTemplateColumns: hc, gap: 4, paddingTop: 6, borderTop: '1px solid var(--ink-100)' }}>
          {colTot.map((v, j) => <span key={j} style={{ textAlign: 'center', fontSize: '12.5px', fontWeight: 700, color: 'var(--ink-600)', fontVariantNumeric: 'tabular-nums' }}>{v || ''}</span>)}
        </span>
        <span style={{ textAlign: 'right', paddingTop: 6, borderTop: '1px solid var(--ink-100)', fontSize: '14px', fontWeight: 800, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{grand}</span>
        <span style={{ paddingTop: 6, borderTop: '1px solid var(--ink-100)', alignSelf: 'stretch' }} />
      </div>
    </div>
  );
}

/** Une commande dans le panneau de droite : son numéro, sa date, ses conditions et ses montants. */
function TermsCard({ label, num, today, value, totals, mail, onChange }: {
  label: string;
  /** La ligne d'envoi — à qui la commande partira, ou pourquoi elle ne partira pas. */
  mail: { text: string; warn: boolean };
  /** Le numéro de la commande — `null` tant qu'elle ne porte aucune pièce, donc ne partira pas. */
  num: GroupNumber | null;
  today: string;
  value: EditorConditions;
  totals: ReturnType<typeof groupTotals>;
  onChange: (next: Partial<EditorConditions>) => void;
}) {
  const r = readConditions(value);
  const bad = !r.ok ? r.field : null;
  const dateProblem = orderDateProblem(value.orderedOn, today);
  const field = (k: 'discount' | 'shipping' | 'earlyPayment' | 'earlyPaymentDays'): CSSProperties => ({
    width: '100%', height: 34, padding: '0 10px', border: `1px solid ${bad === k ? 'var(--rose-fg)' : 'var(--ink-200)'}`,
    borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', fontSize: '13px', color: 'var(--ink-900)', outline: 'none',
    boxSizing: 'border-box', fontVariantNumeric: 'tabular-nums', textAlign: 'right',
  });
  const lab: CSSProperties = { display: 'block', fontSize: '11.5px', fontWeight: 600, color: 'var(--ink-600)', marginBottom: 4 };
  const line = (l: string, v: string, strong = false) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', color: strong ? 'var(--ink-900)' : 'var(--ink-600)', fontWeight: strong ? 800 : 400, fontSize: strong ? '13.5px' : undefined, marginTop: strong ? 2 : 0 }}>
      <span>{l}</span><span>{v}</span></div>
  );
  const pct = r.ok && r.discountBp ? ` (${value.discount.trim()} %)` : '';
  const earlyOn = r.ok && (r.earlyPaymentBp ?? 0) > 0 && totals.pieces > 0;
  // ⚠️ UN GROUPE NOMMÉ PAR LE FOURNISSEUR : une commande par fournisseur, mêmes champs sur chaque carte.
  return (
    <div role="group" aria-label={label} style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ flex: 1, minWidth: 0, fontSize: '13.5px', fontWeight: 700, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      </div>
      <span data-mail-line style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: -6, fontSize: '11.5px', color: mail.warn ? 'var(--amber-fg)' : 'var(--ink-500)', minWidth: 0 }}>
        <Icon name="mail" size={13} />
        <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{mail.text}</span>
      </span>
      {num && (
        <label><span style={lab}>{t('N° de commande — proposé, modifiable')}</span>
          <input value={num.number} onChange={(e) => onChange({ number: typedDocumentNumber(e.target.value) })} autoComplete="off" spellCheck={false}
            aria-invalid={num.problem ? true : undefined}
            style={{ ...field('discount'), border: `1px solid ${num.problem ? 'var(--rose-fg)' : 'var(--ink-200)'}`, textAlign: 'left', fontFamily: MONO }} /></label>
      )}
      {num?.problem && <span role="alert" style={{ marginTop: -4, fontSize: '11.5px', fontWeight: 600, color: 'var(--rose-fg)' }}>{numberProblemText(num.problem, 'order')}</span>}
      <label><span style={lab}>{t('Date de commande')}</span>
        <input type="date" value={value.orderedOn} max={today} onChange={(e) => onChange({ orderedOn: e.target.value })}
          aria-invalid={dateProblem ? true : undefined}
          style={{ ...field('discount'), border: `1px solid ${dateProblem ? 'var(--rose-fg)' : 'var(--ink-200)'}`, textAlign: 'left' }} /></label>
      {dateProblem && <span role="alert" style={{ marginTop: -4, fontSize: '11.5px', fontWeight: 600, color: 'var(--rose-fg)' }}>{dateProblemText(dateProblem)}</span>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <label><span style={lab}>{t('Remise fourn. %')}</span>
          <input value={value.discount} onChange={(e) => onChange({ discount: e.target.value })} inputMode="decimal" placeholder="0" style={field('discount')} /></label>
        <label><span style={lab}>{t('Port {symbole} HT', { symbole: currentCurrency().symbol })}</span>
          <input value={value.shipping} onChange={(e) => onChange({ shipping: e.target.value })} inputMode="decimal" placeholder="0" style={field('shipping')} /></label>
        <label><span style={lab}>{t('Escompte %')}</span>
          <input value={value.earlyPayment} onChange={(e) => onChange({ earlyPayment: e.target.value })} inputMode="decimal" placeholder="0" style={field('earlyPayment')} /></label>
        <label><span style={lab}>{t('si paiement sous (j)')}</span>
          <input value={value.earlyPaymentDays} onChange={(e) => onChange({ earlyPaymentDays: e.target.value })} inputMode="numeric" placeholder="—" style={field('earlyPaymentDays')} /></label>
      </div>
      {bad && <span role="alert" style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--rose-fg)' }}>{t('{field} ne se lit pas — deux décimales au plus, entre 0 et 100 %.', { field: conditionField(bad) })}</span>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 8, borderTop: '1px solid var(--ink-100)', fontSize: '12.5px', fontVariantNumeric: 'tabular-nums' }}>
        {line(t('Brut HT'), euros(totals.grossCents))}
        {line(t('Remise'), totals.discountCents ? `− ${euros(totals.discountCents)}${pct}` : '—')}
        {line(t('Port'), totals.shippingCents ? euros(totals.shippingCents) : '—')}
        {line(t('Net HT'), euros(totals.netCents), true)}
        {earlyOn && (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, color: 'var(--teal-800)', fontWeight: 600 }}>
            <span>{value.earlyPaymentDays.trim()
              ? t('Escompte {v} % à {n} j', { v: value.earlyPayment.trim(), n: Number(value.earlyPaymentDays) })
              : t('Escompte {v} %', { v: value.earlyPayment.trim() })}</span>
            <span>{t('net à payer {v}', { v: euros(totals.netToPayCents) })}</span>
          </div>
        )}
        {totals.unpricedLines > 0 && (
          <span style={{ color: 'var(--amber-fg)', fontWeight: 600 }}>{t('{n} ligne sans prix — non comptée dans le brut', { n: totals.unpricedLines })}</span>
        )}
      </div>
    </div>
  );
}

/**
 * Une marque sans fournisseur dans le panneau de droite : son numéro, et le rattachement qui la débloque.
 * ⚠️ NI DATE NI CONDITIONS ICI : elles appartiennent au fournisseur, qu'on ne connaît pas encore ; rattachée,
 *    la commande les reçoit de sa fiche. Un BL de cette marque s'enregistre au jour même.
 */
function OrphanCard({ kind, brand, num, suppliers, suppliersErr, onNumber, onAttach, onCreateSupplier }: {
  kind: EditorKind;
  brand: OrderBrand;
  num: GroupNumber | null;
  suppliers: { code: string; label: string }[] | null;
  suppliersErr: string | null;
  onNumber: (number: string) => void;
  onAttach: (supplierCode: string) => Promise<void>;
  onCreateSupplier: (label: string) => Promise<string>;
}) {
  const lab: CSSProperties = { display: 'block', fontSize: '11.5px', fontWeight: 600, color: 'var(--ink-600)', marginBottom: 4 };
  return (
    <div data-orphan={brand.id} style={{ background: 'var(--paper)', border: '1.5px solid var(--amber-fg)', borderRadius: 'var(--r-md)', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <span style={{ width: 30, height: 30, borderRadius: 'var(--r-xs)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon name="alert" size={15} stroke={2} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: '13.5px', fontWeight: 700, color: 'var(--ink-900)' }}>{t('{brand} — fournisseur à définir', { brand: brand.label })}</span>
          <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-600)', lineHeight: 1.45, marginTop: 2 }}>
            {kind === 'order' ? t('Cette marque n’est rattachée à aucun fournisseur : sans destinataire, sa commande ne peut pas partir. Le brouillon reste possible.')
              : t('Cette marque n’est rattachée à aucun fournisseur. Le brouillon est possible ; l’entrée en stock attend le rattachement.')}</span>
        </span>
      </div>
      {num && (
        <label><span style={lab}>{kind === 'order' ? t('N° de commande — proposé, modifiable') : t('N° du BL — proposé, modifiable')}</span>
          <input value={num.number} onChange={(e) => onNumber(typedDocumentNumber(e.target.value))} autoComplete="off" spellCheck={false}
            aria-invalid={num.problem ? true : undefined}
            style={{ width: '100%', height: 34, padding: '0 10px', border: `1px solid ${num.problem ? 'var(--rose-fg)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', fontSize: '13px', color: 'var(--ink-900)', outline: 'none', boxSizing: 'border-box', fontFamily: MONO }} /></label>
      )}
      {num?.problem && <span role="alert" style={{ marginTop: -4, fontSize: '11.5px', fontWeight: 600, color: 'var(--rose-fg)' }}>{numberProblemText(num.problem, kind)}</span>}
      <Picker id={`orphan-${brand.id}`} variant="field" label={t('Rattacher la marque {brand} à…', { brand: brand.label })}
        options={(suppliers ?? []).map((x) => ({ value: x.code, label: x.label }))} allLabel={t('Choisir un fournisseur')}
        value="" onChange={(code) => { if (code) void onAttach(code); }}
        onCreate={onCreateSupplier} createLabel={t('Créer le fournisseur')}
        loading={suppliers === null && suppliersErr === null} unavailable={suppliersErr !== null}
        unavailableReason={suppliersErr ? t('Les fournisseurs n’ont pas pu être lus : {motif}', { motif: suppliersErr }) : undefined} />
      <span style={{ fontSize: '11.5px', color: 'var(--ink-500)', lineHeight: 1.45 }}>
        {t('Enregistré une fois pour toutes dans les référentiels : les prochaines commandes et réceptions de {brand} iront directement chez ce fournisseur.', { brand: brand.label })}</span>
    </div>
  );
}

/**
 * Un BL dans le panneau de droite : son numéro, sa date, la commande qu'il solde et sa valeur.
 * ⚠️ NI REMISE NI PORT : une réception n'en porte pas — la valeur est celle des pièces au prix d'achat affiché.
 */
function DeliveryCard({ label, num, today, value, link, totals, onChange, onUnlink }: {
  label: string;
  /** Le numéro du BL — `null` tant qu'il ne porte aucune pièce, donc ne partira pas. */
  num: GroupNumber | null;
  today: string;
  value: EditorConditions;
  /** La commande que ce BL solde — `null` : livraison à la volée. */
  link: string | null;
  totals: ReturnType<typeof groupTotals>;
  onChange: (next: Partial<EditorConditions>) => void;
  onUnlink: () => void;
}) {
  const dateProblem = orderDateProblem(value.orderedOn, today);
  const lab: CSSProperties = { display: 'block', fontSize: '11.5px', fontWeight: 600, color: 'var(--ink-600)', marginBottom: 4 };
  const field = (bad: boolean): CSSProperties => ({
    width: '100%', height: 34, padding: '0 10px', border: `1px solid ${bad ? 'var(--rose-fg)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-ctrl)',
    background: 'var(--paper)', fontSize: '13px', color: 'var(--ink-900)', outline: 'none', boxSizing: 'border-box',
  });
  // ⚠️ UN GROUPE NOMMÉ PAR LE FOURNISSEUR : un BL par fournisseur, mêmes champs sur chaque carte.
  return (
    <div role="group" aria-label={label} style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <span style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      {num && (
        <label><span style={lab}>{t('N° du BL — proposé, modifiable')}</span>
          <input value={num.number} onChange={(e) => onChange({ number: typedDocumentNumber(e.target.value) })} autoComplete="off" spellCheck={false}
            aria-invalid={num.problem ? true : undefined} style={{ ...field(!!num.problem), fontFamily: MONO }} /></label>
      )}
      {num?.problem && <span role="alert" style={{ marginTop: -4, fontSize: '11.5px', fontWeight: 600, color: 'var(--rose-fg)' }}>{numberProblemText(num.problem, 'deliveryNote')}</span>}
      <label><span style={lab}>{t('Date du BL')}</span>
        <input type="date" value={value.orderedOn} max={today} onChange={(e) => onChange({ orderedOn: e.target.value })}
          aria-invalid={dateProblem ? true : undefined} style={{ ...field(!!dateProblem), fontVariantNumeric: 'tabular-nums' }} /></label>
      {dateProblem && <span role="alert" style={{ marginTop: -4, fontSize: '11.5px', fontWeight: 600, color: 'var(--rose-fg)' }}>{dateProblemText(dateProblem)}</span>}
      {link && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px 7px 11px', borderRadius: 'var(--r-sm)', background: 'var(--teal-100)', color: 'var(--teal-800)' }}>
          <Icon name="link" size={13} stroke={2.2} style={{ flexShrink: 0 }} />
          <span style={{ flex: 1, minWidth: 0, fontSize: '12px', fontWeight: 600 }}>
            <Trans k="Rattaché à <1>{number}</1>" vars={{ number: link }} parts={[(x) => <span style={{ fontFamily: MONO, fontWeight: 700 }}>{x}</span>]} /></span>
          <button type="button" onClick={onUnlink} aria-label={t('Détacher la commande')} title={t('Détacher — les quantités restent')}
            style={{ width: 24, height: 24, border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent', color: 'currentColor', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, cursor: 'pointer' }}>
            <Icon name="x" size={12} stroke={2.4} />
          </button>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 8, borderTop: '1px solid var(--ink-100)', fontSize: '12.5px', fontVariantNumeric: 'tabular-nums' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, color: 'var(--ink-900)', fontWeight: 800 }}>
          <span>{t('Valeur HT')}</span><span>{euros(totals.grossCents)}</span></div>
        {totals.unpricedLines > 0 && (
          <span style={{ color: 'var(--amber-fg)', fontWeight: 600 }}>{t('{n} ligne sans prix — non comptée dans la valeur', { n: totals.unpricedLines })}</span>
        )}
      </div>
    </div>
  );
}

/** Un bouton de « Partir de… ». */
function StartTool({ on, label, desc, icon, onClick }: { on: boolean; label: string; desc: string; icon: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} aria-expanded={on} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 12px', border: `1px solid ${on ? 'var(--teal-400)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-sm)', background: on ? 'var(--teal-100)' : 'var(--paper)', textAlign: 'left', cursor: 'pointer' }}>
      <span style={{ width: 30, height: 30, borderRadius: 'var(--r-xs)', background: 'var(--ink-100)', color: 'var(--ink-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d={icon} /></svg>
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: 'var(--ink-900)' }}>{label}</span>
        <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--ink-500)' }}>{desc}</span>
      </span>
    </button>
  );
}

type Incoming = { model: OrderModel; qty: Record<string, number>; pa?: string };

/** « Dupliquer une commande » : ses lignes s'ajoutent à la saisie, prix d'achat compris. */
function DuplicateOrder({ onApply }: { onApply: (incoming: Incoming[], label: string, skipped: number) => void }) {
  const [orders, setOrders] = useState<CommandeResume[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [dq, setDq] = useState('');
  useEffect(() => {
    api.orders().then((r) => setOrders(r.rows)).catch((e) => setFailure(e instanceof Error ? e.message : String(e)));
  }, []);
  const needle = dq.trim().toLowerCase();
  const shown = (orders ?? []).filter((o) => !needle || `${o.number} ${o.supplierLabel ?? ''} ${o.note ?? ''}`.toLowerCase().includes(needle)).slice(0, 8);

  const pick = async (number: string) => {
    try {
      const order = await api.order(number);
      const keys = [...new Set(order.lines.map((l) => l.modelKey).filter((k): k is string => !!k))];
      const models = keys.length ? (await api.orderEditorModels(keys)).rows.filter((m) => m.supplier) : [];
      const incoming = models.map((m) => {
        const lines = order.lines.filter((l) => l.modelKey === m.modelKey);
        const priced = lines.find((l) => l.unitPriceFine != null);
        // ⚠️ Seules les lignes qui ont une case sont reprises ; les autres sont COMPTÉES dans le message.
        const qty = orderableQty(m, Object.fromEntries(lines.map((l) => [l.gencode, l.orderedQty]))).qty;
        return { model: m, qty, pa: priced ? inputFromFine(priced.unitPriceFine) : undefined };
      });
      const kept = incoming.reduce((n, x) => n + Object.keys(x.qty).length, 0);
      onApply(incoming, number, order.lines.length - kept);
    } catch (e) { setFailure(e instanceof Error ? e.message : String(e)); }
  };

  return (
    <div style={{ background: 'var(--paper)', border: '1px solid var(--teal-300)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
      <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--ink-100)' }}>
        <input value={dq} onChange={(e) => setDq(e.target.value)} placeholder={t('N°, fournisseur, référence…')} aria-label={t('Chercher une commande à dupliquer')}
          style={{ width: '100%', height: 34, padding: '0 10px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', fontSize: '13px', color: 'var(--ink-900)', outline: 'none', boxSizing: 'border-box' }} />
      </div>
      {failure && <div role="alert" style={{ padding: 12, fontSize: '12px', color: 'var(--rose-fg)', fontWeight: 600 }}>{failure}</div>}
      {orders === null && !failure && <div style={{ padding: 12, fontSize: '12px', color: 'var(--ink-500)' }}>{t('Lecture des commandes…')}</div>}
      {shown.map((o) => (
        <button key={o.number} type="button" onClick={() => void pick(o.number)} style={{ display: 'block', width: '100%', padding: '9px 12px', border: 'none', borderBottom: '1px solid var(--ink-100)', background: 'transparent', textAlign: 'left', cursor: 'pointer' }}>
          <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ fontFamily: MONO, fontSize: '11.5px', fontWeight: 700, color: 'var(--ink-800)' }}>{o.number}</span>
            <span style={{ fontSize: '11.5px', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>{t('{pieces} · {lines}', { pieces: t('{n} pièce', { n: o.resume.ordered }), lines: t('{n} ligne', { n: o.lineCount }) })}</span>
          </span>
          <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-600)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{`${o.supplierLabel ?? '—'} · ${o.note ?? '—'}`}</span>
        </button>
      ))}
      {orders !== null && shown.length === 0 && <div style={{ padding: 12, textAlign: 'center', fontSize: 'var(--t-dense)', color: 'var(--ink-400)' }}>{t('Aucune commande ne correspond.')}</div>}
      <div style={{ padding: '7px 12px', background: 'var(--ink-50)', fontSize: '11px', color: 'var(--ink-500)' }}>{t('Ses lignes s’ajoutent à la saisie — prix d’achat compris.')}</div>
    </div>
  );
}

/** Une commande ouverte, telle que l'outil la propose. */
type OpenOrder = { number: string; supplierId: string | null; supplierLabel: string | null; remaining: number };

/**
 * « Une commande ouverte » : son RESTE À LIVRER s'ajoute à la saisie, au prix NET de la commande, et le BL lui est
 * rattaché.
 * ⚠️ LE PRIX EST NET DE LA REMISE DE LA COMMANDE — le même calcul que le serveur (`netUnitPriceFine`) : le BL reçoit
 *    ce prix, et le reprendre brut compterait la remise de travers au PMP.
 * ⚠️ UNE LIGNE QUE LA GRILLE NE PEUT PAS MONTRER N'ENTRE PAS EN SILENCE : elle est comptée dans le message.
 */
function OpenOrderTool({ onApply }: { onApply: (incoming: Incoming[], order: OpenOrder, pieces: number, skipped: number) => void }) {
  const [orders, setOrders] = useState<OpenOrder[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [dq, setDq] = useState('');
  useEffect(() => {
    api.openOrders().then((r) => setOrders(r.rows)).catch((e) => setFailure(e instanceof Error ? e.message : String(e)));
  }, []);
  const needle = dq.trim().toLowerCase();
  const shown = (orders ?? []).filter((o) => !needle || `${o.number} ${o.supplierLabel ?? ''}`.toLowerCase().includes(needle)).slice(0, 8);

  const pick = async (o: OpenOrder) => {
    try {
      const order = await api.order(o.number);
      const due = order.lines.filter((l) => l.orderedQty > l.receivedQty);
      const keys = [...new Set(due.map((l) => l.modelKey).filter((k): k is string => !!k))];
      const models = keys.length ? (await api.orderEditorModels(keys)).rows.filter((m) => groupKeyOf(m) !== null) : [];
      const incoming = models.map((m) => {
        const lines = due.filter((l) => l.modelKey === m.modelKey);
        const priced = lines.find((l) => l.unitPriceFine != null);
        const qty = orderableQty(m, Object.fromEntries(lines.map((l) => [l.gencode, l.orderedQty - l.receivedQty]))).qty;
        return { model: m, qty, pa: priced ? inputFromFine(netUnitPriceFine(priced.unitPriceFine!, order.discountBp)) : undefined };
      });
      const pieces = incoming.reduce((n, x) => n + Object.values(x.qty).reduce((a, b) => a + b, 0), 0);
      const kept = incoming.reduce((n, x) => n + Object.keys(x.qty).length, 0);
      onApply(incoming, o, pieces, due.length - kept);
    } catch (e) { setFailure(e instanceof Error ? e.message : String(e)); }
  };

  return (
    <div style={{ background: 'var(--paper)', border: '1px solid var(--teal-300)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
      <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--ink-100)' }}>
        <input value={dq} onChange={(e) => setDq(e.target.value)} placeholder={t('N°, fournisseur…')} aria-label={t('Chercher une commande ouverte')}
          style={{ width: '100%', height: 34, padding: '0 10px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', fontSize: '13px', color: 'var(--ink-900)', outline: 'none', boxSizing: 'border-box' }} />
      </div>
      {failure && <div role="alert" style={{ padding: 12, fontSize: '12px', color: 'var(--rose-fg)', fontWeight: 600 }}>{failure}</div>}
      {orders === null && !failure && <div style={{ padding: 12, fontSize: '12px', color: 'var(--ink-500)' }}>{t('Lecture des commandes…')}</div>}
      {shown.map((o) => (
        <button key={o.number} type="button" onClick={() => void pick(o)} style={{ display: 'block', width: '100%', padding: '9px 12px', border: 'none', borderBottom: '1px solid var(--ink-100)', background: 'transparent', textAlign: 'left', cursor: 'pointer' }}>
          <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ fontFamily: MONO, fontSize: '11.5px', fontWeight: 700, color: 'var(--ink-800)' }}>{o.number}</span>
            <span style={{ fontSize: '11.5px', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>{t('{n} pièce à livrer', { n: o.remaining })}</span>
          </span>
          <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-600)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.supplierLabel ?? '—'}</span>
        </button>
      ))}
      {orders !== null && shown.length === 0 && <div style={{ padding: 12, textAlign: 'center', fontSize: 'var(--t-dense)', color: 'var(--ink-400)' }}>{t('Aucune commande ouverte ne correspond.')}</div>}
      <div style={{ padding: '7px 12px', background: 'var(--ink-50)', fontSize: '11px', color: 'var(--ink-500)' }}>{t('Son reste à livrer s’ajoute à la saisie, et le BL lui est rattaché.')}</div>
    </div>
  );
}

/**
 * « Réassort sur ventes » : les quantités vendues d'un fournisseur entre deux dates choisies.
 * ⚠️ DES DATES LIBRES (décision utilisateur) : les raccourcis 30 et 90 jours ne font que poser la date
 *    de début ; un magasin qui réassortit tous les quatre jours choisit sa période.
 */
function RestockFromSales({ onApply }: { onApply: (incoming: Incoming[], label: string) => void }) {
  const [suppliers, setSuppliers] = useState<{ id: string; label: string }[] | null>(null);
  const [sup, setSup] = useState<{ id: string; label: string } | null>(null);
  const [from, setFrom] = useState(localDay(-30));
  const [to, setTo] = useState(localDay(0));
  const [preview, setPreview] = useState<{ rows: { modelKey: string; gencode: string; qty: number }[]; truncated: boolean; limit: number } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  useEffect(() => {
    api.referentielSuppliers()
      .then((r) => setSuppliers(r.rows.map((s) => ({ id: masterDataId.supplier(s.code), label: s.label }))))
      .catch((e) => setFailure(e instanceof Error ? e.message : String(e)));
  }, []);
  useEffect(() => {
    setPreview(null);
    if (!sup || !from || !to || from > to) return;
    let alive = true;
    api.orderRestock(sup.id, from, to)
      .then((r) => { if (alive) { setPreview(r); setFailure(null); } })
      .catch((e) => { if (alive) setFailure(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; };
  }, [sup, from, to]);

  const chip = (on: boolean): CSSProperties => ({
    height: 28, padding: '0 10px', border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-pill)',
    background: on ? 'var(--teal-500)' : 'var(--paper)', color: on ? '#fff' : 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer',
  });
  const models = new Set(preview?.rows.map((r) => r.modelKey) ?? []);
  const sold = preview?.rows.reduce((n, r) => n + r.qty, 0) ?? 0;
  const text = !sup ? t('Choisissez un fournisseur : chaque article vendu sur la période arrive avec ses quantités vendues, par coloris et taille.')
    : from > to ? t('La date de début suit la date de fin.')
      : !preview ? t('Lecture des ventes…')
        : preview.rows.length === 0 ? t('Aucune vente sur la période chez {s}.', { s: sup.label })
          : t('{articles} · {pieces} chez {s}. Ces quantités remplacent la saisie de ces articles.', { articles: t('{n} article', { n: models.size }), pieces: t('{n} pièce vendue', { n: sold }), s: sup.label });
  const ready = !!preview && preview.rows.length > 0;

  const apply = async () => {
    if (!ready || !sup) return;
    try {
      const found = (await api.orderEditorModels([...models])).rows;
      const incoming = found.map((m) => ({ model: m, qty: Object.fromEntries(preview!.rows.filter((r) => r.modelKey === m.modelKey).map((r) => [r.gencode, r.qty])) }));
      onApply(incoming, sup.label);
    } catch (e) { setFailure(e instanceof Error ? e.message : String(e)); }
  };

  const date: CSSProperties = { width: '100%', height: 32, padding: '0 8px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', fontSize: 'var(--t-dense)', color: 'var(--ink-900)', outline: 'none', boxSizing: 'border-box' };
  return (
    <div style={{ background: 'var(--paper)', border: '1px solid var(--teal-300)', borderRadius: 'var(--r-md)', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div>
        <span style={{ display: 'block', fontSize: '11.5px', fontWeight: 600, color: 'var(--ink-600)', marginBottom: 4 }}>{fieldLabel('supplier')}</span>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {(suppliers ?? []).map((s) => <button key={s.id} type="button" onClick={() => setSup(s)} style={chip(sup?.id === s.id)}>{s.label}</button>)}
        </div>
      </div>
      <div>
        <span style={{ display: 'block', fontSize: '11.5px', fontWeight: 600, color: 'var(--ink-600)', marginBottom: 4 }}>{t('Période de ventes')}</span>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
          <button type="button" onClick={() => { setFrom(localDay(-30)); setTo(localDay(0)); }} style={chip(false)}>{t('30 derniers jours')}</button>
          <button type="button" onClick={() => { setFrom(localDay(-90)); setTo(localDay(0)); }} style={chip(false)}>{t('90 derniers jours')}</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <label><span style={{ display: 'block', fontSize: '11px', color: 'var(--ink-500)', marginBottom: 2 }}>{t('Du')}</span><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={date} /></label>
          <label><span style={{ display: 'block', fontSize: '11px', color: 'var(--ink-500)', marginBottom: 2 }}>{t('Au')}</span><input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={date} /></label>
        </div>
      </div>
      <p style={{ margin: 0, fontSize: 'var(--t-dense)', color: 'var(--ink-600)', lineHeight: 1.5 }}>{text}</p>
      {preview?.truncated && <p style={{ margin: 0, fontSize: 'var(--t-dense)', color: 'var(--amber-fg)', fontWeight: 600 }}>{t('Liste bornée à {n} lignes — resserrez la période.', { n: preview.limit })}</p>}
      {failure && <p role="alert" style={{ margin: 0, fontSize: 'var(--t-dense)', color: 'var(--rose-fg)', fontWeight: 600 }}>{failure}</p>}
      <button type="button" onClick={() => void apply()} aria-disabled={!ready || undefined}
        style={{ height: 36, border: 'none', borderRadius: 'var(--r-ctrl)', background: ready ? 'var(--ink-900)' : 'var(--ink-200)', color: ready ? 'var(--paper)' : 'var(--ink-400)', fontSize: '13px', fontWeight: 700, cursor: ready ? 'pointer' : 'not-allowed' }}>
        {t('Ajouter la proposition')}</button>
    </div>
  );
}
